// /src/Sections/WhyChooseUs/index.jsx
import React, { useEffect, useRef, useState } from "react";
import "./WhyChooseUs.css";
import { whyChooseUs } from "../../data";
import { useTranslation } from "react-i18next";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, FreeMode, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";

// --- Load all logos dynamically (CRA/Webpack) ---
const ctx = require.context(
  "../../assets/companiesServed",
  false,
  /\.(png|jpe?g|webp|svg)$/i
);
const logos = ctx.keys().map((k) => ({
  src: ctx(k),
  alt: k
    .replace("./", "")
    .replace(/\.(png|jpe?g|webp|svg)$/i, "")
    .replace(/[-_]/g, " ")
    .trim(),
}));

/* ---- tiny reveal-on-scroll hook ---- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

const WhyChooseUs = () => {
  const { t } = useTranslation();

  const [headRef, headIn] = useReveal();
  const [featuresRef, featuresIn] = useReveal();
  const [logosRef, logosIn] = useReveal();
  const [prevEl, setPrevEl] = useState(null);
  const [nextEl, setNextEl] = useState(null);

  // helpers to get translated copy for feature cards (fallback to provided text)
  const getTitle = (item) => (item.tTitle ? t(item.tTitle) : item.title);
  const getDesc  = (item) => (item.tDesc  ? t(item.tDesc)  : item.description);

  return (
    <section
      id="why-choose-us"
      role="region"
      aria-label={t("wcu.ariaRegion", { defaultValue: "Why choose us" })}
    >
      {/* Header */}
      <header
        ref={headRef}
        className={`wcw__header reveal reveal--up ${headIn ? "in" : ""}`}
      >
        <h2 className="heading reveal-item" style={{ transitionDelay: "80ms" }}>
          {t("wcu.heading")}
        </h2>
        <div
          className="wcw__accent-line reveal-item"
          style={{ transitionDelay: "120ms" }}
        ></div>

        <p className="subheading reveal-item" style={{ transitionDelay: "180ms" }}>
          {t("wcu.subheading", {
            defaultValue:
              "We deliver professional security solutions backed by experienced personnel, rapid response times, and a commitment to safety-first practices."
          })}
        </p>
      </header>

      {/* Features Grid */}
      <div
        ref={featuresRef}
        className={`features-grid reveal reveal--up ${featuresIn ? "in" : ""}`}
      >
        {whyChooseUs.map((item, index) => (
          <div
            key={index}
            className="feature-card reveal-item"
            style={{ transitionDelay: `${(index + 1) * 120}ms` }}
          >
            <div className="feature-icon">{item.icon}</div>
            <h3 className="feature-title">{getTitle(item)}</h3>
            <p className="feature-description">{getDesc(item)}</p>
          </div>
        ))}
      </div>

      {/* Section Divider */}
      <div className="section-divider">
        <div className="divider-line"></div>
        <span className="divider-text">
          {t("wcu.trustedBy", { defaultValue: "Trusted by Industry Leaders" })}
        </span>
        <div className="divider-line"></div>
      </div>

      {/* Companies served – logo carousel */}
      <div
        ref={logosRef}
        className={`logos-carousel reveal reveal--up ${logosIn ? "in" : ""}`}
        aria-label={t("wcu.companiesServed", { defaultValue: "Companies served" })}
      >
        <div className="carousel-navigation">
          <button
            ref={(node) => setPrevEl(node)}
            className="carousel-btn carousel-btn-prev"
            aria-label={t("wcu.prev", { defaultValue: "Previous companies" })}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button
            ref={(node) => setNextEl(node)}
            className="carousel-btn carousel-btn-next"
            aria-label={t("wcu.next", { defaultValue: "Next companies" })}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <Swiper
          modules={[Autoplay, Navigation]}
          navigation={{ prevEl, nextEl }}
          loop={true}
          loopFillGroupWithBlank={false}
          loopedSlides={logos.length}
          slidesPerView={3}
          spaceBetween={120}
          autoplay={{
            delay: 2000,
            disableOnInteraction: false,
            pauseOnMouseEnter: false,
            reverseDirection: false,
          }}
          speed={1500}
          allowTouchMove={true}
          watchSlidesProgress={true}
          breakpoints={{
            480: {
              slidesPerView: 3,
              spaceBetween: 120,
            },
            640: {
              slidesPerView: 4,
              spaceBetween: 80,
            },
            768: {
              slidesPerView: 5,
              spaceBetween: 60,
            },
            1024: {
              slidesPerView: 6,
              spaceBetween: 40,
            },
            1280: {
              slidesPerView: 7,
              spaceBetween: 30,
            },
          }}
        >
          {[...logos, ...logos, ...logos].map((img, i) => (
            <SwiperSlide key={`${img.src}-${i}`}>
              <div
                className="logo-wrap"
                style={{ transitionDelay: `${(i % 8) * 60}ms` }}
              >
                <img loading="lazy" src={img.src} alt={img.alt} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default WhyChooseUs;
